import { create } from 'zustand';
import { productsApi } from '../api/productsApi';

export type ContentUpdateKind = 'bonus' | 'vault';

interface SeenContent {
  bonusIds: string[];
  vaultPackIds: string[];
}

type SeenContentByAccount = Record<string, SeenContent>;

interface ContentUpdatesState {
  accountId: string | null;
  bonusIds: string[];
  vaultPackIds: string[];
  bonusHasNew: boolean;
  vaultHasNew: boolean;
  checked: boolean;
  bonusOpened: boolean;
  vaultOpened: boolean;
  refresh: (accountId: string) => Promise<void>;
  markSeen: (kind: ContentUpdateKind, accountId: string) => void;
  reset: () => void;
}

export const CONTENT_SEEN_STORAGE_KEY = 'Lyrixa.contentSeen';

function readSeenContent(): SeenContentByAccount {
  try {
    const parsed = JSON.parse(localStorage.getItem(CONTENT_SEEN_STORAGE_KEY) ?? '{}') as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as SeenContentByAccount) : {};
  } catch {
    return {};
  }
}

function seenFor(accountId: string): SeenContent {
  const saved = readSeenContent()[accountId];
  return {
    bonusIds: Array.isArray(saved?.bonusIds) ? saved.bonusIds : [],
    vaultPackIds: Array.isArray(saved?.vaultPackIds) ? saved.vaultPackIds : [],
  };
}

function persistSeen(accountId: string, patch: Partial<SeenContent>): void {
  try {
    const all = readSeenContent();
    all[accountId] = { ...seenFor(accountId), ...patch };
    localStorage.setItem(CONTENT_SEEN_STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Persistence is an enhancement; the in-memory marker still works without storage.
  }
}

const INITIAL_STATE = {
  accountId: null,
  bonusIds: [],
  vaultPackIds: [],
  bonusHasNew: false,
  vaultHasNew: false,
  checked: false,
  bonusOpened: false,
  vaultOpened: false,
} satisfies Omit<ContentUpdatesState, 'refresh' | 'markSeen' | 'reset'>;

export const useContentUpdatesStore = create<ContentUpdatesState>((set, get) => ({
  ...INITIAL_STATE,

  async refresh(accountId) {
    if (get().accountId !== accountId) set({ ...INITIAL_STATE, accountId });
    try {
      const availability = await productsApi.getContentAvailability();
      if (get().accountId !== accountId) return;
      const seen = seenFor(accountId);
      const bonusOpened = get().bonusOpened;
      const vaultOpened = get().vaultOpened;
      if (bonusOpened) persistSeen(accountId, { bonusIds: availability.bonusIds });
      if (vaultOpened) persistSeen(accountId, { vaultPackIds: availability.vaultPackIds });
      set({
        bonusIds: availability.bonusIds,
        vaultPackIds: availability.vaultPackIds,
        checked: true,
        bonusOpened: false,
        vaultOpened: false,
        bonusHasNew:
          !bonusOpened && availability.bonusIds.some((id) => !seen.bonusIds.includes(id)),
        vaultHasNew:
          !vaultOpened && availability.vaultPackIds.some((id) => !seen.vaultPackIds.includes(id)),
      });
    } catch {
      // A failed background check must not interfere with navigation.
    }
  },

  markSeen(kind, accountId) {
    if (get().accountId !== accountId) return;
    if (kind === 'bonus') {
      persistSeen(accountId, { bonusIds: get().bonusIds });
      set({ bonusHasNew: false, bonusOpened: !get().checked });
      return;
    }
    persistSeen(accountId, { vaultPackIds: get().vaultPackIds });
    set({ vaultHasNew: false, vaultOpened: !get().checked });
  },

  reset() {
    set(INITIAL_STATE);
  },
}));
